import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function Works() {
  const [activeFilter, setActiveFilter] = useState('all');
  
  const projects = [
    {
      id: 1,
      title: "Distributed AI Inference Processing System",
      category: "Backend",
      image: "/Screenshot 2025-07-02 203607.png",
      description: "An asynchronous distributed AI inference platform built with Spring Boot, RabbitMQ message queues, Dockerized FastAPI workers, and Redis caching. Reduces inference latency from 1.8s to 420ms and scales to 500+ requests under load.",
      tags: ["Spring Boot", "FastAPI", "RabbitMQ", "Redis", "Docker", "AWS (EC2, S3)", "Microservices", "RESTful APIs", "Concurrency"],
      github: "https://github.com/bunnysunny24/Distributed-AI-Platform"
    },
    {
      id: 2,
      title: "Secure Banking Transaction Processing System",
      category: "Backend",
      image: "/Screenshot 2025-05-08 224741.png",
      description: "A secure banking transactional backend featuring role-based authorization (RBAC), audit logging, and automated reconciliation. Employs MySQL optimistic locking and ACID transactions to safely manage high-concurrency client updates.",
      tags: ["Spring Boot", "Spring Security", "JWT", "MySQL", "Redis", "Hibernate/JPA", "ACID", "Optimistic Locking", "Database Design"],
      github: "https://github.com/bunnysunny24/Banking-System"
    },
    {
      id: 3,
      title: "LogiStream: Real-Time Logistics Intelligence",
      category: "AI & ML",
      image: "/Screenshot 2025-07-02 202047.png",
      description: "A streaming Retrieval-Augmented Generation (RAG) assistant for tracking and risk analysis of shipment data. Implemented vector pipelines via FAISS, Pathway, and Hugging Face, achieving 99.7% retrieval accuracy.",
      tags: ["Python", "Pathway", "RAG", "FAISS", "Hugging Face", "React.js", "Vector Search", "Real-time ETL", "Anomaly Detection"],
      github: "https://github.com/bunnysunny24/LogiStream"
    },
    {
      id: 4,
      title: "TaskMate: Real-Time Collaborative Platform",
      category: "Full Stack",
      image: "/Screenshot 2025-05-08 225101.png",
      description: "A collaborative task manager featuring secure multi-user workspaces and real-time state synchronization via persistent WebSockets, providing sub-second updates across active users.",
      tags: ["Spring Boot", "React.js", "WebSockets", "MySQL", "Spring Security", "JWT", "Hibernate/JPA", "Tailwind CSS"],
      github: "https://github.com/bunnysunny24/TaskMate"
    },
    {
      id: 5,
      title: "ACORP: Traffic Congestion Optimizer & Resource Planner",
      category: "AI & ML",
      image: "/Screenshot 2025-05-08 224147.png",
      description: "An event-driven platform for predictive congestion analytics and operational scheduling using FastAPI, Streamlit dashboards, and predictive neural network models.",
      tags: ["FastAPI", "PostgreSQL", "Streamlit", "Docker", "TensorFlow", "Predictive Analytics", "RESTful APIs", "Modular Architecture"],
      github: "https://github.com/bunnysunny24/ACORP"
    },
    {
      id: 6,
      title: "AgriSync: AI-Powered Smart Farming System",
      category: "Full Stack",
      image: "/image.png",
      description: "A smart agriculture ecosystem combining deep learning crop disease classifiers with decentralized blockchain marketplace escrow structures to ensure secure direct-to-consumer transactions.",
      tags: ["React.js", "TensorFlow", "FastAPI", "Solidity", "Smart Contracts", "Ethereum", "EfficientNet", "XGBoost", "Tailwind CSS"],
      link: "https://agri-sync.vercel.app",
      github: "https://github.com/bunnysunny24/AgriSync"
    }
  ];

  const categories = ['all', 'Backend', 'AI & ML', 'Full Stack'];
  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="works" className="py-24 md:py-32">
      <div className="text-center mb-16">
        <span className="text-sm font-bold text-accent uppercase tracking-wider">Portfolio</span>
        <h2 className="text-4xl md:text-5xl font-bold text-primary mt-2 mb-4 relative inline-block">
          My Works
          <span className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-accent rounded-full"></span>
        </h2>
        <p className="text-lg max-w-2xl mx-auto mt-8 text-gray-600 dark:text-gray-300">
          Here are some of my recent projects that showcase my skills and expertise.
        </p>
      </div>

      {/* Filter Categories */}
      <div className="flex flex-wrap justify-center mb-12 gap-4">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setActiveFilter(category)}
            className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${
              activeFilter === category
                ? 'bg-primary text-white font-medium shadow-md'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {category === 'all' ? 'All' : category}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10">
        {filteredProjects.map((project, index) => (
          <div 
            key={project.id} 
            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-card hover:shadow-xl transition-all duration-500 group animate-fade-in opacity-0" 
            style={{ animationDelay: `${0.1 * index}s` }}
          >
            <div className="relative h-64 overflow-hidden">
              <img 
                src={project.image} 
                alt={project.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-primary/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <button className="bg-white dark:bg-gray-800 text-primary px-6 py-3 rounded-full font-medium shadow-button transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  View Project
                </button>
              </div>
              <div className="absolute top-4 left-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-medium shadow-sm dark:text-white">
                {project.category}
              </div>
              
              {/* New: Project number with design element */}
              <div className="absolute bottom-4 right-4 w-12 h-12 bg-accent/80 backdrop-blur-sm text-white rounded-full flex items-center justify-center font-bold shadow-highlight">
                0{project.id}
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="text-xl font-bold mb-3 text-primary group-hover:text-accent transition-colors duration-300">{project.title}</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{project.description}</p>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {project.tags.map((tag, i) => (
                  <span 
                    key={i} 
                    className="text-xs px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary dark:text-gray-200 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              {/* Link with arrow */}
              <div className="mt-6 flex justify-end">
                <a 
                  href={project.github || project.link || `#project-${project.id}`} 
                  className="flex items-center text-primary font-medium hover:text-accent transition-colors duration-300 group/link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>See Details</span>
                  <svg className="w-5 h-5 ml-2 transform group-hover/link:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="text-center mt-16">
        <Link to="/projects" className="group relative overflow-hidden border-2 border-primary text-primary hover:text-white px-10 py-4 rounded-full font-medium transition-all duration-300 shadow-button hover:shadow-lg inline-block">
          <span className="relative z-10">View All Projects</span>
          <span className="absolute inset-0 bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
        </Link>
      </div>
    </section>
  );
}

export default Works;